import React from "react";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import HomePage from "@/pages/HomePage";
import ListingsPage from "@/pages/ListingsPage";
import MembershipsPage from "@/pages/MembershipsPage";
import BusinessDashboardPage from "@/pages/BusinessDashboardPage";
import BuyerProfilePage from "@/pages/BuyerProfilePage";
import SellerProfilePage from "@/pages/SellerProfilePage";
import AddPartPage from "@/pages/AddPartPage";
import BulkAddPartsPage from "@/pages/BulkAddPartsPage";
import RegistrationPage from "@/pages/RegistrationPage";
import NotFoundPage from "@/pages/NotFoundPage";
import Layout from "@/components/Layout";
import { Toaster } from "@/components/ui/toaster";
import { ThemeProvider } from "@/components/ThemeProvider";
import AboutUsPage from "@/pages/AboutUsPage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import FaqPage from "@/pages/FaqPage";
import TermsPage from "@/pages/TermsPage";
import PartSearchResultsPage from "@/pages/PartSearchResultsPage";
import PartInquiryFormPage from "@/pages/PartInquiryFormPage";
import AddOnsPage from "@/pages/AddOnsPage";
import ContactUsPage from "@/pages/ContactUsPage";
import EnterpriseSalesPage from "@/pages/EnterpriseSalesPage";
import AiAssistant from "@/components/AiAssistant";
import MessagesPage from "@/pages/MessagesPage";
import PaymentPage from "@/pages/PaymentPage";
import UpgradeMembershipPage from "@/pages/UpgradeMembershipPage";
import Providers from "./store/Providers";
import AuthInitializer from "./components/AuthInitializer";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import PartsByCategory from "./pages/PartsByCategory";
import BillingPage from "./pages/BillingPage";
import ScrollToTop from "./components/ScrollToTop";
import SavedParts from "./pages/SavedParts";
import AiSearchResultsPage from "./pages/AiSearchResultsPage";
import InquiryChatHandler from "./pages/InquiryChatHandler";
import PartDetailPage from "./pages/PartDetailPage";
import VendorPublicPage from "./pages/VendorPublicPage";
import Notifications from "./pages/Notifications";
import usePageViews from "./utils/usePageView";
import GlobalTrialObserver from "./components/GlobalTrialObserver";

const stripeKey = import.meta.env.VITE_STRIPE_LIVE_KEY;
const stripePromise = loadStripe(stripeKey);

const AppContent = () => {
  usePageViews();
  return (
    <>
      <InquiryChatHandler />
      <AuthInitializer>
        <GlobalTrialObserver />
        <Elements stripe={stripePromise}>
          <div className="flex flex-col min-h-screen">
            <Layout>
              <ScrollToTop />
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/listings" element={<ListingsPage />} />
                <Route
                  path="/listings/category/:categoryId"
                  element={<PartsByCategory />}
                />
                <Route path="/register" element={<RegistrationPage />} />
                <Route path="/list-inventory" element={<MembershipsPage />} />
                <Route path="/:slug" element={<VendorPublicPage />} />
                <Route
                  path="/partDetail/:partId"
                  element={<PartDetailPage />}
                />
                <Route
                  path="/upgrade-membership"
                  element={<UpgradeMembershipPage />}
                />
                <Route path="/payment" element={<PaymentPage />} />
                {/* <Route path="/add-ons" element={<AddOnsPage />} /> */}
                {/* <Route path="/add-ons/:addOnId" element={<AddOnsPage />} /> */}
                <Route path="/profile/saved-parts" element={<SavedParts />} />
                <Route
                  path="/profile/notifications"
                  element={<Notifications />}
                />
                <Route path="/dashboard" element={<BusinessDashboardPage />} />
                <Route
                  path="/dashboard/profile"
                  element={<SellerProfilePage />}
                />
                <Route
                  path="/dashboard/settings"
                  element={<SellerProfilePage />}
                />
                <Route path="/dashboard/billing" element={<BillingPage />} />
                <Route path="/profile" element={<BuyerProfilePage />} />
                <Route
                  path="/profile/settings"
                  element={<BuyerProfilePage />}
                />
                <Route path="/profile/messages" element={<MessagesPage />} />
                <Route path="/dashboard/add-part" element={<AddPartPage />} />
                <Route
                  path="/dashboard/bulk-add-parts"
                  element={<BulkAddPartsPage />}
                />
                <Route path="/about" element={<AboutUsPage />} />
                <Route path="/privacy" element={<PrivacyPolicyPage />} />
                <Route path="/faq" element={<FaqPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/contact-us" element={<ContactUsPage />} />
                <Route
                  path="/enterprise-sales"
                  element={<EnterpriseSalesPage />}
                />
                <Route
                  path="/ai-search-results"
                  element={<AiSearchResultsPage />}
                />
                <Route
                  path="/search-results"
                  element={<PartSearchResultsPage />}
                />
                <Route path="/inquire-part" element={<PartInquiryFormPage />} />
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
              <AiAssistant />
            </Layout>
          </div>
        </Elements>
        <Toaster />
      </AuthInitializer>
    </>
  );
};

function App() {
  return (
    <Providers>
      <ThemeProvider defaultTheme="system" storageKey="vite-ui-theme">
        <Router>
          <AppContent />
        </Router>
      </ThemeProvider>
    </Providers>
  );
}

export default App;
